import React, { Suspense, useEffect, useRef } from "react";
import useSWR from "swr";
import { fetcher, getRequest, staticOptions } from "lib/core/apiClient";
import useSendAnalyticsEvent from "lib/hooks/useAnalytics";
import { analyticsEvents, analyticsTypes } from "lib/utils/constants";
import { useLocation } from "react-router-dom";
import { getCODShippingHandles, getItems } from "lib/utils/checkout";
import PaymentMethods from "components/payments/methods";
import { usePaymentContext } from "lib/contexts/PaymentProvider";
import PaymentMethodDialog from "components/payments/dialogs/PaymentMethodDialog";
import { useLoader } from "lib/contexts/LoaderContext";
import PaymentProcessingDialog from "components/payments/dialogs/PaymentProcessing";
import MarketingConsent from "components/common/MarketingConsent";
import { AnimateLoading } from "components/animation/AnimateLoading";
import {
  findModeSpecificCartAlteration,
  getReviewAutomationPayload,
  parsePaymentsResponse,
} from "lib/utils/payments";
import { useCheckoutContext } from "lib/contexts/CheckoutProvider";
import { useLocale } from "lib/hooks/useLocale";
import { PaymentSkeleton } from "components/common/skeleton/PaymentSkeleton";
import { useMerchantContext } from "lib/contexts/MerchantProvider";
import { useSubscriptionContext } from "lib/contexts/SubscriptionProvider";
import SubscriptionPaymentNotice from "components/subscriptions/SubscriptionPaymentNotice";

const Payments = React.memo(() => {
  const { t } = useLocale();
  const {
    state: { checkoutId, billing, shippingHandles, appliedCoupons },
    actions: { setCheckoutItems, setBilling },
  } = useCheckoutContext();
  const { sendAnalyticsEvent } = useSendAnalyticsEvent();
  const {
    state: { paymentMethods },
    actions: { setPaymentMethods, setPaymentMethodDialog },
  } = usePaymentContext();
  const {
    state: { merchant },
  } = useMerchantContext();
  const {
    state: { mode: purchaseMode },
  } = useSubscriptionContext();

  const { setIsLoading } = useLoader();

  const { hash: hashParam } = useLocation();

  const { data: paymentsData, isValidating: isPaymentsLoading } = useSWR<any>(
    () => {
      return checkoutId ? `/checkout/${checkoutId}/payments` : null;
    },
    (url: string) => {
      return getRequest(url, "KRATOS_PRIVATE");
    },
    {
      ...staticOptions,
      onError: () => {
        sendAnalyticsEvent({
          eventName: analyticsEvents.FLO_PAYMENT_FAILED,
          eventType: "load",
          metaData: {
            checkoutId,
          },
        });
      },
    },
  );

  useEffect(() => {
    if (Boolean(checkoutId) && Boolean(merchant?.merchantId)) {
      sendAnalyticsEvent({
        eventName: analyticsEvents.FLO_PAYMENT_LOADED,
        eventType: "load",
        metaData: {
          checkoutId: checkoutId,
          merchantId: merchant?.merchantId,
        },
      });
    }
  }, [checkoutId, merchant?.merchantId]);

  useEffect(() => {
    if (!Boolean(paymentsData) || isPaymentsLoading) return;

    const { modes: parsedModes, items: parsedItems } = parsePaymentsResponse(paymentsData);

    const automationPayload = getReviewAutomationPayload(parsedModes);

    if (Boolean(automationPayload.length)) {
      sendAnalyticsEvent({
        eventName: analyticsEvents.FLO_PAYMENT_REFRESHED,
        eventFor: [analyticsTypes.SF_ANALYTICS],
        eventType: "load",
        metaData: {
          automationData: automationPayload,
        },
      });
    }

    //Adding the transient prepaid freebie to the cart items. This conditionally adds if the item(s) is not added to the cart already
    const containsPrepaidFreebie = findModeSpecificCartAlteration(parsedModes);
    if (Boolean(containsPrepaidFreebie)) {
      setCheckoutItems(getItems(parsedItems));
    }

    const prepaidDiscount = parsedModes?.[0]?.amount || 0;
    setBilling({ ...billing, prepaid_total: prepaidDiscount });
    setPaymentMethods(parsedModes);
    setIsLoading(false);
  }, [paymentsData, isPaymentsLoading, appliedCoupons, purchaseMode]);

  //TODO: Handle this
  useEffect(() => {
    if (
      Boolean(hashParam?.length) &&
      hashParam === "#COD" &&
      !Boolean(getCODShippingHandles(shippingHandles)?.length > 1)
    ) {
      setPaymentMethodDialog("COD");
    }
  }, [shippingHandles, hashParam]);

  if (!isPaymentsLoading && !Boolean(paymentMethods)) return <></>;

  return (
    <>
      <AnimateLoading loading={isPaymentsLoading}>
        <AnimateLoading.Skeleton>
          <PaymentSkeleton />
        </AnimateLoading.Skeleton>
        <AnimateLoading.Content>
          <SubscriptionPaymentNotice />
          <Suspense fallback={<PaymentSkeleton />}>
            <PaymentMethods />
          </Suspense>
          <PaymentMethodDialog />
          <PaymentProcessingDialog />
          <MarketingConsent />
        </AnimateLoading.Content>
      </AnimateLoading>
    </>
  );
});

export default Payments;
